import React, { useState, useContext } from "react";

import Typography from "@mui/material/Typography";

import { UserAvatar } from "components/Avatar/UserAvatar";
import { EditorStateToHtml } from "components/TextEditors/EditorStateToHtml";
import { DateWithTooltip } from "components/DateWithTooltip";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { dateFormat } from "core/datetime";
import { IconButton } from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { ConfirmationDialog } from "components/ConfirmationDialog";
import { UserProfileContext } from "contexts/UserProfileContext";
import { IComment, ICommentContent } from "interfaces/comment";
import { UsersContext } from "contexts/UsersContext";
import { CommentEditor } from "components/Comments/CommentEditor";

interface CommentFeedItemProps {
  comment: IComment;
  updateComment: (content: ICommentContent) => Promise<IComment>;
  deleteComment: () => Promise<void>;
}

export const CommentFeedItem = ({
  comment,
  updateComment,
  deleteComment
}: CommentFeedItemProps): JSX.Element => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const { currentUserId } = useContext(UserProfileContext);
  const { getFullName } = useContext(UsersContext);

  const username = getFullName(comment.create_user);
  const isEditable = comment.create_user === currentUserId;

  return (
    <CommentFeedItemWrapper>
      <UserAvatar data-qa="CP-Avatar-Comment-User" name={username} />
      <CommentContentWrapper>
        <CommentMetadataWrapper>
          <UserNameTypo>{username}</UserNameTypo>
          <TimestampTypo>
            <DateWithTooltip
              data-qa="CP-DateTime-Comment-DateTime"
              date={comment.create_date}
              showTime
            />
          </TimestampTypo>
          {comment.create_date !== comment.change_date ? (
            <Tooltip
              title={
                comment.change_date
                  ? dateFormat(comment.change_date, {
                      format: "Do MMM YYYY h:mma"
                    } as any)
                  : ""
              }
            >
              <EditTypography>Edited</EditTypography>
            </Tooltip>
          ) : null}
        </CommentMetadataWrapper>
        <CommentEditorWrapper>
          {editMode ? (
            <CommentEditor
              onSubmit={content => {
                updateComment(content);
                setEditMode(false);
              }}
              onCancel={() => setEditMode(false)}
              comment={comment}
            />
          ) : (
            <>
              {comment.content ? (
                <EditorStateToHtml rawState={comment.content} />
              ) : null}
            </>
          )}
        </CommentEditorWrapper>
      </CommentContentWrapper>
      {isEditable && !editMode && (
        <CommentActionsWrapper className="comment-actions">
          <IconButton
            size="small"
            aria-label="edit comment"
            onClick={() => setEditMode(true)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="delete comment"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </CommentActionsWrapper>
      )}
      {deleteDialogOpen && (
        <ConfirmationDialog
          open={deleteDialogOpen}
          title="Are you sure you want to delete this comment?"
          onConfirm={() => {
            deleteComment();
            setDeleteDialogOpen(false);
          }}
          onCancel={() => setDeleteDialogOpen(false)}
          onClose={() => setDeleteDialogOpen(false)}
        />
      )}
    </CommentFeedItemWrapper>
  );
};

const EditTypography = styled(Typography)`
  font-size: 10px;
  line-height: 14px;
  color: #7f7f7f;
  text-decoration: underline;
  text-decoration-style: dotted;
`;

const UserNameTypo = styled(Typography)`
  font-size: 14px;
  font-weight: 600;
  line-height: 14px;
`;

const TimestampTypo = styled(Typography)`
  font-size: 10px;
  line-height: 12px;
  color: #7f7f7f;
`;

const CommentFeedItemWrapper = styled("div")`
  display: flex;
  align-items: top;

  // we only want to show the available actions when hovering over the comment item
  .comment-actions {
    visibility: hidden;
  }

  :hover {
    .comment-actions {
      visibility: visible;
    }
  }
`;

const CommentContentWrapper = styled("div")`
  margin: 5px 0 0 8px;
  display: flex;
  flex-flow: column wrap;
  flex: 1;
`;

const CommentMetadataWrapper = styled("div")`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CommentActionsWrapper = styled("div")`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding-top: 1px;
`;

const CommentEditorWrapper = styled("div")`
  word-break: break-word;
  width: 100%;
  padding-right: 6px;
`;
